import {
  DEFAULT_PREFIX,
  DEFAULT_VALIDATION_MESSAGE_TYPE,
  VALIDATION_MESSAGE_TYPES,
} from '../constants';
import {
  configVue2StyleClassAttrs,
} from '../config';
import {
  h,
  Comment,
  Text,
} from 'vue';

let UNIQUE_ID_COUNTER = 0;
let TIMER;

// selector to find focusable not hidden inputs
const FOCUSABLE_SELECTOR_NOT_HIDDEN = 'input:not([type=hidden]):not(:disabled)';
// selector to find focusable not disables elements
const FOCUSABLE_SELECTOR_NOT_DISABLED = 'select:not(:disabled),textarea:not(:disabled),button:not(:disabled)';
// // selector to find focusable not hidden and disabled elements
const FOCUSABLE_SELECTOR_NOT_HIDDEN_DISABLED = `${FOCUSABLE_SELECTOR_NOT_HIDDEN},${FOCUSABLE_SELECTOR_NOT_DISABLED}`;
// selector to find focusable elements
const FOCUSABLE_SELECTOR = `a,frame,iframe,${FOCUSABLE_SELECTOR_NOT_HIDDEN_DISABLED},*[tabindex]`;

const scheduler = typeof setImmediate === 'function' ? setImmediate : setTimeout;

export function getUniqueString (prefix = DEFAULT_PREFIX) {
  return `${prefix}${UNIQUE_ID_COUNTER++}`;
}

/**
 * Returns a random element from array
 * @param array - the array to return a random element from
 * @param {string} seed - use a string to seed the randomization, so it returns the same element each time
 * based on that string.
 * @returns {*} - the random element
 */
export function getRandomElement (array, seed) {
  if (seed) {
    const hash = javaHashCode(seed);
    return array[Math.abs(hash) % array.length];
  } else {
    return array[getRandomInt(array.length)];
  }
}

/**
 * Returns a hash code for a string.
 * (Compatible to Java's String.hashCode())
 * We use this algo to be in sync with android.
 *
 * The hash code for a string object is computed as
 *     s[0]*31^(n-1) + s[1]*31^(n-2) + ... + s[n-1]
 * using number arithmetic, where s[i] is the i th character
 * of the given string, n is the length of the string,
 * and ^ indicates exponentiation.
 * (The hash value of the empty string is zero.)
 *
 * @param {string} str a string
 * @return {number} a hash code value for the given string.
 */
export function javaHashCode (str) {
  let h;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(31, h) + str.charCodeAt(i) | 0;
  }

  return h;
}

/**
 * Generate a random integer
 * @param {number} max - max range of integer to generate
 * @returns {number} randomly generated integer between 0 and max
 */
export function getRandomInt (max) {
  return Math.floor(Math.random() * max);
}

export function formatMessages (messages) {
  if (!messages) {
    return [];
  }

  return messages.map(message => {
    if (typeof message === 'string') {
      return {
        message,
        type: DEFAULT_VALIDATION_MESSAGE_TYPE,
      };
    }

    return message;
  });
}

export function filterFormattedMessages (formattedMessages) {
  const validationState = getValidationState(formattedMessages);

  if (!formattedMessages || !validationState) {
    return [];
  }

  return formattedMessages.filter(message => !!message.message && message.type === validationState);
}

/*
 * The priority order of message types is as flows: 'error' > 'warning' > 'success'.
 * If any message of type 'error' is present in messages, the input state is considered
 * to be 'error', then 'warning' and lastly 'success'.
 */
export function getValidationState (formattedMessages) {
  if (!formattedMessages) {
    return null;
  }

  if (hasFormattedMessageOfType(formattedMessages, VALIDATION_MESSAGE_TYPES.ERROR)) {
    return VALIDATION_MESSAGE_TYPES.ERROR;
  }
  if (hasFormattedMessageOfType(formattedMessages, VALIDATION_MESSAGE_TYPES.WARNING)) {
    return VALIDATION_MESSAGE_TYPES.WARNING;
  }
  if (hasFormattedMessageOfType(formattedMessages, VALIDATION_MESSAGE_TYPES.SUCCESS)) {
    return VALIDATION_MESSAGE_TYPES.SUCCESS;
  }

  return null;
}

export function hasFormattedMessageOfType (formattedMessages, messageType) {
  if (!formattedMessages || !messageType) {
    return false;
  }

  return formattedMessages.some(message => message?.type === messageType);
}

export function findFirstFocusableNode (element) {
  return element?.querySelector(FOCUSABLE_SELECTOR);
}

/* html-fragment component:
 * To render html without wrapping in another element as when using v-html.
 * props: html
 */
export const htmlFragment = (props) => {
  return h('div', { innerHTML: props.html });
};

export const flushPromises = () => {
  return new Promise((resolve) => {
    scheduler(resolve);
  });
};

/*
  It is very cumbersome to check if a slot is empty in vue 3. Copied this method from the following thread
  https://github.com/vuejs/core/issues/4733. There is an RFC to fix this but not yet being worked on.
  https://github.com/vuejs/rfcs/discussions/453
*/
export function hasSlotContent (slot, slotProps = {}) {
  if (!slot) return false;

  // eslint-disable-next-line complexity
  return slot(slotProps).some((vnode) => {
    if (vnode.type === Comment) return false;

    if (Array.isArray(vnode.children) && !vnode.children.length) return false;

    return (
      vnode.type !== Text ||
      (typeof vnode.children === 'string' && vnode.children.trim() !== '')
    );
  });
}

/**
 * Transform a string from kebab-case to PascalCase
 * @param string
 * @returns {string}
 */
export const kebabCaseToPascalCase = (string) => {
  return string?.toLowerCase()
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

/**
 * Transform a string from PascalCase to kebab-case
 * @param string
 * @returns {string}
 */
export const pascalCaseToKebabCase = (string) => {
  return string
    .replace(/\.?([A-Z0-9]+)/g, (x, y) => '-' + y.toLowerCase())
    .replace(/^-/, '');
};

export const extractVueListeners = (attrs) => {
  const listeners = Object.entries(attrs)
    .filter(([key]) => key.match(/on[A-Z]/));
  return Object.fromEntries(listeners);
};

/**
 * $el works very differently than in vue 2, if the first node in the template is a text node
 * such as a comment it will return that instead of the first actual element. This function
 * will recursively return the first element in the template instead of the first node.
 * @param el
 * @returns {HTMLElement} The first element in the template
 */
export const returnFirstEl = (el) => {
  if (el?.nodeType === Node.ELEMENT_NODE) {
    return el;
  } else if (!el?.nodeType) {
    return null;
  } else {
    return returnFirstEl(el?.nextSibling);
  }
};

/**
  Only will apply changes if the config option configVue2StyleClassAttrs is set to true. It is false by default.

  Removes the class and style attributes from the $attrs. This is useful for vue 2 to vue 3 migration
  purposes so we don't cause breaking changes due to INSTANCE_ATTRS_CLASS_STYLE
  https://v3-migration.vuejs.org/breaking-changes/attrs-includes-class-style

  Remove the class and style attributes from the v-bind like so so v-bind="removeClassStyleAttrs($attrs)",
  and then apply them to the root element manually via:

  :class="$attrs.class"
  :style="$attrs.style"
*/
export function removeClassStyleAttrs (attrs) {
  if (!configVue2StyleClassAttrs) return attrs;
  const listeners = Object.entries(attrs)
    .filter(([key]) => !['class', 'style'].includes(key));
  return Object.fromEntries(listeners);
}

/**
  This should be applied to the root element on components using inheritAttrs: false.
  This will add the class and style attributes back to the root element if configVue2StyleClassAttrs
  is enabled.
*/
export function addClassStyleAttrs (attrs) {
  if (!configVue2StyleClassAttrs) return {};
  return {
    class: attrs.class,
    style: attrs.style,
  };
}

/*
* Set's a global timer to debounce the execution of a function.
* @param { object } func - the function that is going to be called after timeout
* @param { number } [timeout=300] timeout
* */
export function debounce (func, timeout = 300) {
  clearTimeout(TIMER);
  TIMER = setTimeout(func, timeout);
}

/**
 * Checks if the element is out of the viewport
 * https://gomakethings.com/how-to-check-if-any-part-of-an-element-is-out-of-the-viewport-with-vanilla-js/
 * @param  {HTMLElement} element The element to check
 * @return {Object} A set of booleans for each side of the element
 */

export function isOutOfViewPort (element) {
  const bounding = element.getBoundingClientRect();

  const isOut = {
    top: bounding.top < 0,
    left: bounding.left < 0,
    bottom: bounding.bottom > (window.innerHeight || document.documentElement.clientHeight),
    right: bounding.right > (window.innerWidth || document.documentElement.clientWidth),
  };
  isOut.any = Object.values(isOut).some(val => val);
  isOut.all = Object.values(isOut).every(val => val);
  return isOut;
}

// match valid characters for a domain name followed by a dot, e.g. "dialpad."
const domainNameRegex = /(?:(?:[^\s!@#$%^&*()_=+[\]{}\\|;:'",.<>/?]+)\.)/;

// match valid TLDs for a hostname (outdated list from ~2017)
const tldRegerx = new RegExp(
  '(?:' +
  'com|ru|org|net|de|jp|uk|br|it|pl|fr|in|au|ir|info|nl|cn|es|cz|kr|ca|eu|ua|co|gr|' +
  'za|ro|biz|ch|se|tw|mx|vn|hu|be|tr|at|dk|tv|me|ar|sk|no|us|fi|id|cl|xyz|io|pt|by|' +
  'il|ie|nz|kz|hk|lt|cc|my|sg|club|bg|edu|рф|pk|su|top|th|hr|rs|pe|pro|si|az|lv|pw|' +
  'ae|ph|online|ng|ee|ws|ve|cat' +
  ')',
);

// match valid IPv4 addresses, e.g. "192.158.1.38"
const ipv4Regex = new RegExp(
  '(?:(?:[0-9]|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])\\.){3}' +
  '(?:[0-9]|[1-9]\\d|1\\d{2}|2[0-4]\\d|25[0-5])',
);

// match hostnames OR IPv4 addresses, e.g. "dialpad.com" or "192.158.1.38"
const hostnameOrIpRegex = new RegExp(
  '(?:' +
  [
    [
      domainNameRegex.source,
      tldRegerx.source,
    ].join('+'),
    ipv4Regex.source,
  ].join('|') +
  ')',
);

// match URL paths, e.g. "/news"
const urlPathRegex = /(?:(?:[;/][^#?<>\s]*)?)/;

// match URL queries and fragments, e.g. "?cache=1&new=true" or "#heading1"
const urlQueryOrFragmentRegex = /(?:(?:\?[^#<>\s]+)?(?:#[^<>\s]+)?)/;

// match complete hostnames or IPv4 addresses without a protocol and with optional
// URL paths, queries and fragments e.g. "dialpad.com/news?cache=1#heading1"
const urlWithoutProtocolRegex = new RegExp(
  '\\b' +
  [
    hostnameOrIpRegex.source,
    urlPathRegex.source,
    urlQueryOrFragmentRegex.source,
    '(?!\\w)',
  ].join('+'),
);

// match complete hostnames with protocols and optional URL paths, queries and fragments,
// e.g. "ws://localhost:9011" or "https://dialpad.com/news?cache=1#heading1"
const urlWithProtocolRegex = /\b[a-z\d.-]+:\/\/[^<>\s]+/;

// match email addresses with an optional "mailto:" prefix and URL queries, e.g.
// "hey@dialpad.com" or "mailto:hey@dialpad.com?subject=Hi&body=Hey%20there"
const emailAddressRegex = new RegExp(
  '(?:mailto:)?' +
  '[a-z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+/=?^_`{|}~-]+)*@' +
  [
    hostnameOrIpRegex.source,
    urlQueryOrFragmentRegex.source,
  ].join('+') +
  '(?!\\w)',
);

/**
 * Match phone numbers, e.g. "765-8813", "(778) 765-8813" or "+17787658813".
 * @param {number} minLength
 * @param {number} maxLength
 * @returns {RegExp}
 */
export function getPhoneNumberRegex (minLength = 7, maxLength = 15) {
  // Some older browser versions don't support lookbehind, so provide a RegExp
  // version without it. It fails just one test case, so IMO it's still good
  // enough to use. https://caniuse.com/js-regexp-lookbehind
  try {
    return new RegExp(
      '(?:^|(?<=\\W))' +
      '(?![\\s\\-])\\+?(?:[0-9()\\- \\t]' +
      `{${minLength},${maxLength}}` +
      ')(?=\\b)(?=\\W(?=\\W|$)|\\s|$)',
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('This browser doesn\'t support regex lookahead/lookbehind');
  }

  return new RegExp(
    '(?![\\s\\-])\\+?(?:[0-9()\\- \\t]' +
      `{${minLength},${maxLength}}` +
      ')(?=\\b)(?=\\W(?=\\W|$)|\\s|$)',
  );
}

const phoneNumberRegex = getPhoneNumberRegex();

// match all link types
export const linkRegex = new RegExp(
  [
    urlWithoutProtocolRegex.source,
    urlWithProtocolRegex.source,
    emailAddressRegex.source,
    phoneNumberRegex.source,
  ].join('|'),
  'gi',
);

/**
 * Check if a string is a phone number. Validates only exact matches.
 * @param {string|number} input
 * @returns {boolean}
 */
export function isPhoneNumber (input) {
  if (!input || (!['string', 'number'].includes(typeof input))) return false;
  input = input.toString();
  return phoneNumberRegex.exec(input)?.[0] === input;
}

/**
 * Check if a string is an URL. Validates only exact matches.
 * @param {string} input
 * @returns {boolean}
 */
export function isURL (input) {
  if (!input || typeof input !== 'string') return false;
  return urlWithoutProtocolRegex.exec(input)?.[0] === input ||
    urlWithProtocolRegex.exec(input)?.[0] === input;
}

/**
 * Check if a string is an email address. Validates only exact matches.
 * @param {string} input
 * @returns {boolean}
 */
export function isEmailAddress (input) {
  if (!input || typeof input !== 'string') return false;
  return emailAddressRegex.exec(input)?.[0] === input;
}

/**
 * Concatenate a string removing null or undefined elements
 * avoiding parsing them as string with template strings
 * @param {Array} elements
 * @returns {String}
 */
export function safeConcatStrings (elements) {
  return elements.filter(str => !!str).join(' ');
}

/**
 * Locale safe function to capitalize the first letter of a string.
 * @param {string} str the string to capitalize the first letter of
 * @param {string} locale a string representing the locale to be used. Defaults to 'en-US'
 * @returns The passed in string with the first letter capitalized
 */
export function capitalizeFirstLetter (str, locale = 'en-US') {
  return str.replace(/^\p{CWU}/u, char => char.toLocaleUpperCase(locale));
}

/**
 * Warns if the component is not mounted properly. Useful for tests.
 * @param {HTMLElement} componentRef - the component reference
 * @param {string} componentName - the component name
 */
// eslint-disable-next-line complexity
export function warnIfUnmounted (componentRef, componentName) {
  if (typeof process === 'undefined') return;
  if (process.env.NODE_ENV !== 'test') return;
  if (!componentRef || !(componentRef instanceof HTMLElement) || !document?.body) return;
  if (!document.body.contains(componentRef)) {
    console.warn(`The ${componentName} component is not attached to the document body. This may cause issues.`);
  }
}

/**
 * checks whether the dt-scrollbar is being used on the root element.
 * @param rootElement {HTMLElement}
 * @returns {boolean}
 */
function isDtScrollbarInUse (rootElement = document.documentElement) {
  if (rootElement.hasAttribute('data-overlayscrollbars')) {
    return true;
  }
  return false;
}

/**
 * This will disable scrolling on the root element regardless of whether you are using dt-scrollbar or not.
 * @param rootElement {HTMLElement}
 */
export function disableRootScrolling (rootElement = document.documentElement) {
  if (isDtScrollbarInUse(rootElement)) {
    rootElement.classList.add('d-scrollbar-disabled');
  } else {
    rootElement.classList.add('d-of-hidden');
  }
}

/**
 * This will enable scrolling on the root element regardless of whether you are using dt-scrollbar or not.
 * @param rootElement {HTMLElement}
 */
export function enableRootScrolling (rootElement = document.documentElement) {
  if (isDtScrollbarInUse(rootElement)) {
    rootElement.classList.remove('d-scrollbar-disabled');
  } else {
    rootElement.classList.remove('d-of-hidden');
  }
}

export default {
  getUniqueString,
  getRandomElement,
  getRandomInt,
  formatMessages,
  filterFormattedMessages,
  hasFormattedMessageOfType,
  getValidationState,
  htmlFragment,
  flushPromises,
  kebabCaseToPascalCase,
  extractVueListeners,
  removeClassStyleAttrs,
  addClassStyleAttrs,
  returnFirstEl,
  debounce,
  isOutOfViewPort,
  getPhoneNumberRegex,
  linkRegex,
  isEmailAddress,
  isPhoneNumber,
  isURL,
  safeConcatStrings,
  capitalizeFirstLetter,
  disableRootScrolling,
  enableRootScrolling,
};
